"use client";

import * as React from "react";
import { clsx } from "clsx";
import { GripVertical } from "lucide-react";

import { relativeTime } from "@/lib/reports/time";
import styles from "./Block.module.css";

export type BlockDensity = "compact" | "comfortable" | "spacious";

export interface BlockChipsContext {
  freshness?: { refreshedAt?: string; slaOk?: boolean };
  performanceClass?: "instant" | "interactive" | "slow" | "blocked";
  metricStatus?: "draft" | "experimental" | "certified" | "deprecated";
  audienceSafe?: boolean;
}

export interface BlockAction {
  id: string;
  label: string;
  onSelect?: () => void;
  intent?: string;
}

export interface BlockProps {
  title?: string;
  subtitle?: string;
  density?: BlockDensity;
  editing?: boolean;
  selected?: boolean;
  state?: "idle" | "loading" | "error" | "empty";
  errorMessage?: string;
  chips?: BlockChipsContext;
  /** Right-aligned cluster in the header. */
  headerEnd?: React.ReactNode;
  /** Optional narrative line under body. */
  narrative?: React.ReactNode;
  /** Footer action chips. */
  actions?: BlockAction[];
  /** Called from the error-state retry button. */
  onRetry?: () => void;
  children?: React.ReactNode;
  className?: string;
  /** data-floats-block-id passthrough for the agent runtime + tests. */
  blockId?: string;
  /** Optional shape hint for the loading skeleton — picks the right
   *  silhouette (kpi / bar / line / table) so the placeholder previews
   *  what's coming instead of a generic three-bar shimmer. */
  skeletonShape?: "kpi" | "bar" | "line" | "table" | "default";
}

/**
 * Block
 *
 * Universal report-block shell. Composes a header (with drag handle + title +
 * subtitle + chips + kebab slot), a body slot, optional narrative line,
 * optional action footer. State variants: loading / empty / error.
 */
export function Block({
  title,
  subtitle,
  density = "comfortable",
  editing = false,
  selected = false,
  state = "idle",
  errorMessage,
  chips,
  headerEnd,
  narrative,
  actions,
  onRetry,
  children,
  className,
  blockId,
  skeletonShape = "default",
}: BlockProps) {
  const titleId = blockId ? `block-${blockId}-title` : undefined;
  return (
    <article
      className={clsx(styles.root, className)}
      data-density={density}
      data-editing={editing}
      data-selected={selected}
      data-state={state}
      data-floats-block-id={blockId}
      aria-labelledby={title && titleId ? titleId : undefined}
      aria-label={!title ? "Report block" : undefined}
    >
      <header className={styles.header}>
        <span className={styles.dragHandle} data-drag-handle aria-hidden={true}>
          <GripVertical size={14} strokeWidth={2} />
        </span>
        <div className={styles.headerTitle}>
          {title ? (
            <span id={titleId} className={styles.title} title={title}>{title}</span>
          ) : null}
          {subtitle ? (
            <span className={styles.subtitle} title={subtitle}>{subtitle}</span>
          ) : null}
        </div>
        <div className={styles.headerActions}>
          {chips ? <BlockChips chips={chips} /> : null}
          {headerEnd}
        </div>
      </header>

      <div className={styles.body}>
        {state === "loading" ? (
          <div className={styles.loading}>
            <BlockSkeleton shape={skeletonShape} />
          </div>
        ) : state === "empty" ? (
          <div className={styles.empty}>{children ?? "No data to show."}</div>
        ) : state === "error" ? (
          <div className={styles.error}>
            <span>{errorMessage ?? "Something went wrong."}</span>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className={styles.actionChip}
                style={{ marginLeft: 10 }}
              >
                ↻ Retry
              </button>
            ) : null}
          </div>
        ) : (
          children
        )}
      </div>

      {narrative ? <div className={styles.narrative}>{narrative}</div> : null}

      {actions && actions.length > 0 ? (
        <footer className={styles.footer}>
          {actions.map((a) => (
            <button
              key={a.id}
              type="button"
              className={styles.actionChip}
              onClick={a.onSelect}
              data-intent={a.intent}
            >
              {a.label}
            </button>
          ))}
        </footer>
      ) : null}
    </article>
  );
}

function BlockChips({ chips }: { chips: BlockChipsContext }) {
  return (
    <span className={styles.chipsRow}>
      {chips.metricStatus ? <FreshnessChip label={chips.metricStatus} kind="status" /> : null}
      {chips.performanceClass ? <FreshnessChip label={chips.performanceClass} kind="perf" /> : null}
      {chips.freshness?.refreshedAt ? <FreshnessChip label={relativeTime(chips.freshness.refreshedAt)} kind="freshness" /> : null}
      {chips.audienceSafe ? <FreshnessChip label="Client safe" kind="audience" /> : null}
    </span>
  );
}

function FreshnessChip({ label, kind }: { label: string; kind: "freshness" | "perf" | "status" | "audience" }) {
  const palette: Record<typeof kind, string> = {
    freshness: "rgba(17,24,39,0.06)",
    perf: "rgba(19,49,154,0.08)",
    status: "rgba(229,11,113,0.10)",
    audience: "rgba(16,185,129,0.10)",
  };
  return (
    <span
      style={{
        background: palette[kind],
        color: "var(--text-muted)",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        textTransform: "lowercase",
      }}
    >
      {label}
    </span>
  );
}

function BlockSkeleton({ shape = "default" }: { shape?: "kpi" | "bar" | "line" | "table" | "default" }) {
  // Shape-specific silhouettes so the user sees what's coming.
  if (shape === "kpi") {
    return (
      <div style={{ width: "100%", padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <div className={styles.skeleton} style={{ width: "55%", height: 32 }} />
        <div className={styles.skeleton} style={{ width: "80%", height: 36, marginTop: 4 }} />
      </div>
    );
  }
  if (shape === "bar") {
    return (
      <div style={{ width: "100%", padding: 12, display: "flex", alignItems: "flex-end", gap: 8, height: 120 }}>
        {[60, 80, 45, 95, 70, 30, 85].map((h, i) => (
          <div
            key={i}
            className={styles.skeleton}
            style={{ flex: 1, height: `${h}%`, borderRadius: 6 }}
          />
        ))}
      </div>
    );
  }
  if (shape === "line") {
    return (
      <div style={{ width: "100%", padding: 12 }}>
        <div className={styles.skeleton} style={{ width: "100%", height: 100, borderRadius: 6 }} />
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={styles.skeleton} style={{ flex: 1, height: 8 }} />
          ))}
        </div>
      </div>
    );
  }
  if (shape === "table") {
    return (
      <div style={{ width: "100%", padding: 12, display: "flex", flexDirection: "column", gap: 6 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={styles.skeleton} style={{ width: i === 0 ? "60%" : "100%", height: 12 }} />
        ))}
      </div>
    );
  }
  return (
    <div style={{ width: "100%", padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
      <div className={styles.skeleton} style={{ width: "40%", height: 14 }} />
      <div className={styles.skeleton} style={{ width: "70%", height: 14 }} />
      <div className={styles.skeleton} style={{ width: "90%", height: 60, marginTop: 8 }} />
    </div>
  );
}
